/**
 * Demo / "Try these" assets on S3 + CloudFront (config-only v1 — no DB table).
 *
 * S3-ready files: src/assets/try/s3-upload/  (run scripts/build_demo_s3_upload.py to rebuild)
 * Drag s3-upload/ contents into S3 under static/dashboard/
 */

const CDN_BASE = (
  process.env.REACT_APP_ASSET_CDN_BASE_URL ||
  process.env.REACT_APP_CDN_BASE_URL ||
  "https://dj6wws6ravlf1.cloudfront.net"
).replace(/\/$/, "");

const DEMO_ROOT = "static/dashboard";

export const DEMO_SAMPLE_DISPLAY_COUNT = 5;

export const DEMO_SAMPLE_POOL_KEYS = {
  BG_REMOVE: "bg_remove",
  PRODUCT: "product",
  FOOD: "food",
  AUTOMOBILE: "automobile",
  MARKETPLACE: "marketplaces",
  PASSPORT: "passport",
  DATING: "dating",
  SOCIAL: "social",
};

export function demoAssetUrl(relativePath) {
  const path = relativePath.replace(/^\//, "");
  return `${CDN_BASE}/${DEMO_ROOT}/${path}`;
}

function demoSample(category, id, alt, opts = {}) {
  const base = `samples/${category}/${id}`;
  const thumbExt = opts.thumbExt || "png";
  const originalExt = opts.originalExt || "jpg";
  const removedExt = opts.removedExt || "png";
  const includeOriginal = opts.includeOriginal !== false;
  const bgRemovedUrl = demoAssetUrl(`${base}/bg_removed.${removedExt}`);
  return {
    id,
    alt,
    category,
    src: demoAssetUrl(`${base}/thumb.${thumbExt}`),
    originalUrl: includeOriginal
      ? demoAssetUrl(`${base}/original.${originalExt}`)
      : bgRemovedUrl,
    bgRemovedUrl,
  };
}

function labelFromId(id) {
  return id
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildPool(category, entries) {
  return entries.map(([id, alt, opts = {}]) =>
    demoSample(category, id, alt || labelFromId(id), opts)
  );
}

/** Hero "Try these" + remove-bg flow — assets/try/removebg/ */
export const BG_REMOVE_SAMPLE_POOL = buildPool("bg_remove", [
  ["female", "Portrait sample"],
  ["dog", "Dog sample"],
  ["cat", "Cat sample"],
  ["cat2", "Cat sample"],
  ["dog1", "Dog sample"],
  ["dog2", "Dog sample"],
  ["tiger", "Tiger sample"],
  ["bird", "Bird sample"],
  ["horse", "Horse sample"],
  ["horse-race", "Horse race sample"],
  ["panther", "Panther sample"],
  ["swan", "Swan sample"],
]);

/** Edit Images modal + Product catalogue — assets/try/products/ */
export const PRODUCT_SAMPLE_POOL = buildPool("product", [
  ["dove", "Dove"],
  ["gillette-razor", "Gillette razor"],
  ["shampoo_thumbnail", "Shampoo", { thumbExt: "jpg" }],
  ["soap", "Soap", { thumbExt: "jpg" }],
  ["surf", "Surf", { thumbExt: "jpg" }],
  ["Dettol", "Dettol"],
  ["Oreo", "Oreo", { thumbExt: "jpg" }],
  ["appy-fizz", "Appy Fizz", { originalExt: "png" }],
  ["biscoff-cookies", "Biscoff cookies"],
  ["cadbury-dairy-milk", "Cadbury Dairy Milk"],
  ["daawat-basmati-rice", "Daawat basmati rice"],
  ["dellol-soap", "Dellol soap"],
  ["delmonte-extra-virgin-olive-oil", "Del Monte olive oil"],
  ["freedom-sunflower-oil", "Freedom sunflower oil"],
  ["hit-cockroach-killer-spray-200-ml-quick-pantry", "Hit spray", { originalExt: "png" }],
  ["images", "Product sample"],
  ["india-gate-basmati-rice", "India Gate basmati rice"],
  ["kissan-tomato-ketchup", "Kissan tomato ketchup"],
  ["kit-kat", "Kit Kat"],
  ["mini-oreo", "Mini Oreo"],
  ["nestle-kitkat", "Nestle KitKat", { thumbExt: "jpg" }],
  [
    "Maggi_Tomato_Ketchup_Bottle_1_kg_Quick_Pantry",
    "Maggi ketchup",
    { originalExt: "png" },
  ],
]);

export const FOOD_SAMPLE_POOL = buildPool("food", [
  ["biryani", "Biryani", { thumbExt: "jpg" }],
  ["masala-dosa", "Masala dosa", { thumbExt: "jpg" }],
  ["ice-cream", "Ice cream", { thumbExt: "jpg" }],
  ["pasta", "Pasta", { thumbExt: "jpg" }],
  ["Burger-Bowls", "Burger bowls", { thumbExt: "jpg" }],
  ["Chocolate-Cupcake", "Chocolate cupcake", { thumbExt: "jpg" }],
  ["Laddu", "Laddu", { thumbExt: "jpg" }],
  ["coca-cola", "Coca-Cola", { thumbExt: "jpg" }],
  ["idli_vada", "Idli vada", { thumbExt: "jpg" }],
  ["salad", "Salad", { thumbExt: "jpg" }],
  ["salad2", "Salad", { thumbExt: "jpg" }],
]);

export const AUTOMOBILE_SAMPLE_POOL = buildPool("automobile", [
  ["bmw-1", "BMW"],
  ["car-2", "Car"],
  ["car-3", "Car", { originalExt: "png" }],
  ["car-4", "Car"],
  ["car-5", "Car"],
  ["car-6", "Car"],
  ["car-7", "Car"],
  ["car-8", "Car", { originalExt: "jfif" }],
  ["bike", "Bike"],
  ["bus", "Bus"],
  ["van", "Van"],
  ["tyre", "Tyre"],
  ["tracter", "Tractor"],
  ["John-Deere-544L-Wheel-Loader", "Wheel loader"],
]);

export const MARKETPLACE_SAMPLE_POOL = buildPool("marketplaces", [
  ["rolex", "Rolex watch", { thumbExt: "jpg" }],
  ["perfume", "Perfume", { thumbExt: "jpg" }],
  ["perfume2", "Perfume", { thumbExt: "jpg" }],
  ["shoe", "Shoe", { thumbExt: "jpg" }],
  ["watch", "Watch", { thumbExt: "jpg" }],
  ["Minimalist", "Minimalist product"],
  ["candle", "Candle"],
  ["monster", "Monster", { thumbExt: "jpg" }],
  ["offwhite", "Off-white", { thumbExt: "jpg" }],
  ["shampoo", "Shampoo", { thumbExt: "jpg" }],
]);

/** Passport samples — S3: original.jpg, bg_removed.png, thumb.png */
export const PASSPORT_SAMPLE_POOL = buildPool("passport", [
  ["american", "American passport sample"],
  ["indian", "Indian passport sample"],
  ["japanese", "Japanese passport sample"],
  ["pakistani", "Pakistani passport sample"],
  ["australian", "Australian passport sample"],
]);

/** Dating profile samples — assets/try/dating/ */
export const DATING_SAMPLE_POOL = buildPool("dating", [
  ["american", "American dating profile", { thumbExt: "png", originalExt: "png" }],
  ["indian", "Indian dating profile", { thumbExt: "png", originalExt: "png" }],
  ["african", "African dating profile", { thumbExt: "png", originalExt: "png" }],
  ["europian", "European dating profile", { thumbExt: "png", originalExt: "png" }],
  ["japanese", "Japanese dating profile", { thumbExt: "png", originalExt: "png" }],
  ["japanese-1", "Japanese dating profile", { thumbExt: "png", originalExt: "png" }],
]);

/** Social media — no dedicated S3 folder; reuse product samples. */
export const SOCIAL_SAMPLE_POOL = PRODUCT_SAMPLE_POOL;

const DEMO_SAMPLE_POOLS = {
  [DEMO_SAMPLE_POOL_KEYS.BG_REMOVE]: BG_REMOVE_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.PRODUCT]: PRODUCT_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.FOOD]: FOOD_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.AUTOMOBILE]: AUTOMOBILE_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.MARKETPLACE]: MARKETPLACE_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.PASSPORT]: PASSPORT_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.DATING]: DATING_SAMPLE_POOL,
  [DEMO_SAMPLE_POOL_KEYS.SOCIAL]: SOCIAL_SAMPLE_POOL,
};

/** Category See-all modals — keyed by resize_category.category_id */
export const CATEGORY_DEMO_SAMPLE_POOLS = {
  2: PASSPORT_SAMPLE_POOL,
  3: MARKETPLACE_SAMPLE_POOL,
  5: DATING_SAMPLE_POOL,
  6: PRODUCT_SAMPLE_POOL,
};

const EMPTY_DEMO_SAMPLE_POOL = Object.freeze([]);

export function getDemoSamplePool(poolKey) {
  if (Array.isArray(poolKey)) return poolKey;
  return DEMO_SAMPLE_POOLS[poolKey] ?? EMPTY_DEMO_SAMPLE_POOL;
}

export function getCategoryDemoSamplePool(categoryId) {
  return CATEGORY_DEMO_SAMPLE_POOLS[categoryId] ?? PRODUCT_SAMPLE_POOL;
}

export function pickRandomDemoSamples(pool, count = DEMO_SAMPLE_DISPLAY_COUNT) {
  if (!pool?.length) return [];
  if (pool.length <= count) return [...pool];
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

/** @deprecated Use BG_REMOVE_SAMPLE_POOL + pickRandomDemoSamples */
export const BG_REMOVE_SAMPLES = BG_REMOVE_SAMPLE_POOL.slice(0, DEMO_SAMPLE_DISPLAY_COUNT);

/** @deprecated Use PRODUCT_SAMPLE_POOL */
export const PRODUCT_SAMPLES = PRODUCT_SAMPLE_POOL.slice(0, DEMO_SAMPLE_DISPLAY_COUNT);

/** @deprecated Use FOOD_SAMPLE_POOL */
export const FOOD_SAMPLES = FOOD_SAMPLE_POOL.slice(0, DEMO_SAMPLE_DISPLAY_COUNT);

/** @deprecated Use AUTOMOBILE_SAMPLE_POOL */
export const AUTOMOBILE_SAMPLES = AUTOMOBILE_SAMPLE_POOL.slice(0, DEMO_SAMPLE_DISPLAY_COUNT);

/** @deprecated Use MARKETPLACE_SAMPLE_POOL */
export const MARKETPLACE_SAMPLES = MARKETPLACE_SAMPLE_POOL.slice(0, DEMO_SAMPLE_DISPLAY_COUNT);

export function getCategoryDemoSamples(categoryId) {
  return pickRandomDemoSamples(getCategoryDemoSamplePool(categoryId));
}

export function resolveDemoSample(sampleOrSrc) {
  if (!sampleOrSrc) return null;
  if (typeof sampleOrSrc === "string") {
    return {
      src: sampleOrSrc,
      originalUrl: sampleOrSrc,
      bgRemovedUrl: sampleOrSrc,
    };
  }
  const bgRemovedUrl = sampleOrSrc.bgRemovedUrl || sampleOrSrc.src;
  const originalUrl = sampleOrSrc.originalUrl || sampleOrSrc.src;
  if (!bgRemovedUrl) return null;
  return { ...sampleOrSrc, originalUrl, bgRemovedUrl };
}
